/**
 * The privacy policy and the terms, built from facts about how this service
 * is run. One source for the app's pages, for the copies the server writes
 * into the HTML of /privacy and /terms (readable without JavaScript, as the
 * stores and search engines read them) and for the tests, so that none of
 * them says something the others do not.
 *
 * A fact the operator has not given is left out, never shown as a blank or
 * as "TBD": the stores reject placeholder text. `legalGaps()` lists what is
 * missing, for the operator.
 *
 * In the apps (`native`) nothing is said about paying, and no platform is
 * named: the App Store and Google Play allow neither.
 */
import { DELETED_ACCOUNT_NAME, deletionConfirmText, deletionFacts, SIGNED_OUT_DEVICE_KEPT_DAYS, WEBHOOK_RECORD_KEPT_DAYS } from './accountDeletion.js';
import { REPORT_KEPT_DAYS } from './moderation.js';
import { PENDING_SIGNUP_KEPT_DAYS } from './passwords.js';
import { SIGN_IN_METHOD_NAMES, type SignInMethod } from './protocol.js';

/** When each text last changed in substance. Change the date with the words, and email account holders first about anything important (the texts say so). */
export const PRIVACY_UPDATED = '2026-10-11';
export const TERMS_UPDATED = '2026-10-11';

/** The defaults the operator's settings start from (DECISIONS of 2026-10-11). */
export const LEGAL_DEFAULTS = { operatorName: 'IT Spar', minimumAge: 16, logDays: 30, backupDays: 30 } as const;

/** How long the copy of an email we sent is kept; afterwards only a record that it was sent, with no address or words. */
export const EMAIL_COPY_KEPT_DAYS = 30;
/** How long that record of a sent email is kept, so the same email is never sent twice. */
export const EMAIL_RECORD_KEPT_DAYS = 400;
/** How long a signed-in device stays signed in without being used. */
export const SIGNED_IN_UNUSED_DAYS = 30;

export interface LegalFacts {
  appName: string;
  /** The website's address, such as https://meetdost.com. */
  appUrl: string;
  operator: {
    /** The company that runs the service, as on both store listings (OPERATOR_NAME). */
    name: string;
    /** Its registered address (OPERATOR_ADDRESS). */
    address: string | null;
    /** The law the terms are under, such as "India" (OPERATOR_JURISDICTION). */
    jurisdiction: string | null;
    /** Where disputes go, such as "the courts of Mumbai, India" (OPERATOR_COURTS). */
    courts: string | null;
  };
  contact: {
    /** Questions about the service (SUPPORT_EMAIL). */
    support: string | null;
    /** Questions and requests about personal data (PRIVACY_EMAIL, or the support address). */
    privacy: string | null;
    /** Reports of abuse, from people and authorities (REPORTS_EMAIL, or the support address). */
    reports: string | null;
  };
  /** The ways to sign in that the service offers. */
  signIn: readonly SignInMethod[];
  /** Plans are sold (on the website). */
  sold: boolean;
  /** The words of the apps: nothing about paying, and no platform named. */
  native: boolean;
  storage: {
    /** Where recordings are kept: on the server's own disk, in Amazon S3, or with another service that speaks S3's language (S3_ENDPOINT). */
    recordings: 'server' | 'aws' | 'other';
    /** The AWS region, for recordings in Amazon S3. */
    region: string | null;
    /** Recordings in S3 are delivered through CloudFront. */
    cloudfront: boolean;
  };
  services: {
    /** Who runs the servers and the database, and where (HOSTING_PROVIDER). */
    hosting: string | null;
    /** Who delivers the email (EMAIL_PROVIDER). */
    email: string | null;
  };
  retention: {
    /** CHAT_RETENTION_DAYS; null keeps chat until its host deletes it. */
    chatDays: number | null;
    /** MEETING_HISTORY_DAYS; null keeps a meeting's history until its owner deletes their account. */
    meetingDays: number | null;
    /** BACKUP_RETENTION_DAYS. */
    backupDays: number;
    /** LOG_RETENTION_DAYS. */
    logDays: number;
  };
  /** The youngest age at which someone may have an account (MINIMUM_AGE). */
  minimumAge: number;
}

/** What the server says about itself; the app adds whether it is one of the native apps. */
export type ServerLegalFacts = Omit<LegalFacts, 'native'>;

/** Some text, or a link: a path on this site ("/privacy#delete-account"), an https address or a mailto: address. */
export type Inline = string | { text: string; link: string };

export type LegalBlock = { p: Inline[] } | { list: Inline[][] } | { h3: string };

export interface LegalSection {
  /** The anchor of the section: /privacy#delete-account, /terms#rules. */
  id: string;
  title: string;
  blocks: LegalBlock[];
}

export interface LegalDocument {
  title: string;
  /** ISO date. */
  updated: string;
  intro: Inline[];
  sections: LegalSection[];
}

/** The facts before the server has said anything: only the defaults, and nothing the operator must give. */
export function defaultLegalFacts(appName: string, appUrl: string): LegalFacts {
  return {
    appName,
    appUrl,
    operator: { name: LEGAL_DEFAULTS.operatorName, address: null, jurisdiction: null, courts: null },
    contact: { support: null, privacy: null, reports: null },
    signIn: [],
    sold: false,
    native: false,
    storage: { recordings: 'server', region: null, cloudfront: false },
    services: { hosting: null, email: null },
    retention: { chatDays: null, meetingDays: null, backupDays: LEGAL_DEFAULTS.backupDays, logDays: LEGAL_DEFAULTS.logDays },
    minimumAge: LEGAL_DEFAULTS.minimumAge,
  };
}

/** Details the policy and the terms leave out because the operator has not given them, with the setting that gives each. */
export function legalGaps(f: ServerLegalFacts): Array<{ what: string; setting: string }> {
  return [
    !f.operator.address && { what: 'the company’s registered address', setting: 'OPERATOR_ADDRESS' },
    !f.operator.jurisdiction && { what: 'the law the terms are under', setting: 'OPERATOR_JURISDICTION' },
    !f.contact.privacy && { what: 'an address for privacy questions', setting: 'PRIVACY_EMAIL or SUPPORT_EMAIL' },
    !f.services.hosting && { what: 'who hosts the servers and the database, and where', setting: 'HOSTING_PROVIDER' },
    !f.services.email && { what: 'who delivers the email', setting: 'EMAIL_PROVIDER' },
  ].filter((gap): gap is { what: string; setting: string } => Boolean(gap));
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "11 October 2026", the same in every browser and on the server. */
export function legalDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} ${MONTHS[month! - 1]} ${year}`;
}

const days = (n: number): string => (n === 1 ? '1 day' : `${n} days`);
const mail = (address: string): Inline => ({ text: address, link: `mailto:${address}` });
const p = (...parts: Inline[]): LegalBlock => ({ p: parts });
const list = (...items: Array<Inline | Inline[] | false | null | undefined>): LegalBlock => ({
  list: items.filter((item): item is Inline | Inline[] => Boolean(item)).map((item) => (Array.isArray(item) ? item : [item])),
});

/** "IT Spar, 12 Example Road, Mumbai": the company, with its address when there is one. */
const operatorLine = (f: LegalFacts): string => (f.operator.address ? `${f.operator.name}, ${f.operator.address}` : f.operator.name);

/** The external accounts people sign in with, by name: "Google or Microsoft". */
function externalSignIn(f: LegalFacts): string[] {
  return f.signIn.filter((m) => m !== 'email').map((m) => SIGN_IN_METHOD_NAMES[m]);
}

/** "Google, Microsoft or Apple". */
function either(names: readonly string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} or ${names.at(-1)}`;
}

/** Where to write about personal data: the privacy address, or failing that the support address. */
const privacyContact = (f: LegalFacts): string | null => f.contact.privacy ?? f.contact.support;

export function privacyPolicy(f: LegalFacts): LegalDocument {
  const web = !f.native;
  const external = externalSignIn(f);
  const privacy = privacyContact(f);
  const deletion = deletionFacts({
    appName: f.appName,
    inApp: f.native,
    plansSold: f.sold,
    providers: external,
    backupDays: f.retention.backupDays,
  });
  const hosting = f.services.hosting;
  const { recordings, region } = f.storage;

  const sections: LegalSection[] = [
    {
      id: 'who-we-are',
      title: 'Who we are',
      blocks: [
        p(`${f.appName} is run by ${operatorLine(f)}. This policy covers ${f.appName} on the web at ${f.appUrl.replace(/^https?:\/\//, '')} and in our mobile apps. ${f.operator.name} decides how your personal data is used, and is responsible for it.`),
        ...(privacy ? [p('Questions about your data, and requests about it, go to ', mail(privacy), '.')] : []),
      ],
    },
    {
      id: 'what-we-collect',
      title: 'What we collect, and why',
      blocks: [
        list(
          'Your account: your name, your email address, whether it was confirmed, and how you sign in. We need them to sign you in, to show your name to the people in your meetings, and to email you about your account.',
          f.signIn.some((m) => m === 'google' || m === 'microsoft')
            ? `When you sign in with ${either(f.signIn.filter((m) => m === 'google' || m === 'microsoft').map((m) => SIGN_IN_METHOD_NAMES[m]))}: your name, your email address and the ID it gives your account. We never receive your password there, and we can’t read your mail, files or calendar.`
            : null,
          f.signIn.includes('apple')
            ? 'When you sign in with Apple: the ID Apple gives your account, your name the first time, and an email address, which may be a private address that Apple forwards to you. When you delete your account, we ask Apple to end your Sign in with Apple for us.'
            : null,
          f.signIn.includes('email')
            ? 'When you sign in with an email address and a password: we keep the password only in a scrambled form that can’t be turned back into it.'
            : null,
          `The devices you are signed in on: the browser or app, the kind of device, the network address it last used, how and when it signed in, and when it was last used. We use them for security, and Settings shows them so you can sign any device out. A device’s record is deleted ${days(SIGNED_OUT_DEVICE_KEPT_DAYS)} after it is signed out.`,
          'Your meetings: the links you create (title, time and settings), each time a meeting runs (when it started and ended, and whether it was recorded), and who took part: the name each person joined under, guests included, their role, and when they joined and left.',
          'If you join as a guest: the name you type, kept with the meeting’s history and with your chat messages, and a random number for your browser or app, so the host can admit you or keep you out.',
          'Chat messages sent in a meeting. They are saved with the meeting, so the host, and everyone who took part while signed in, can read them afterwards.',
          'Recordings: the video and sound of a meeting, when a host records it. Everyone in the meeting is told, before they ask to join and by a REC badge while it happens. With a recording, we keep who it is shared with: the email addresses its owner types, and whether its link is on.',
          'Audio and video in a call. They pass through our media server to the other people in the meeting, and are not kept unless the meeting is recorded. Your connection quality is sent too, so the others see when your connection is weak; it is not kept.',
          f.sold ? (web ? 'Your plan: which one, how often it is paid, and when it renews or ends.' : 'Your plan: which one, and when it renews or ends.') : null,
          web && f.sold
            ? 'Payments are handled by Stripe: you type your card details on Stripe’s page and they never reach us. Stripe receives your name and email address with the payment.'
            : null,
          'Reports: when you report someone, what you chose and wrote, who you are (your account, or the name you joined under and an address if you gave one), and a copy of the messages concerned. When someone reports you, the same, with a copy of your messages in that meeting. Only the people who run the service see a report, to decide what to do; the person reported is never told who reported them.',
          'The emails we send you: a copy of each, so we can look into an email that did not arrive.',
          'Technical records: the network address of each request, what it asked for (with any secret codes in it taken out), and when. When the app hits a problem, it sends us a report of what went wrong, on which page, and the app’s version: never your name, your email address, a meeting’s code or what was said.',
          'On your device: what keeps you signed in (in a cookie the page’s scripts can’t read on the web, and in your device’s secure storage in the apps), your camera and microphone choices, and the name you last typed as a guest.',
        ),
        p('We don’t show ads, we don’t track you across other apps or websites, and we don’t sell your data. We don’t collect your location or your contacts.'),
      ],
    },
    {
      id: 'legal-bases',
      title: 'Why we may use it',
      blocks: [
        p('Where the law asks for a reason (in the European Economic Area and the United Kingdom, for example), ours are:'),
        list(
          'to provide the service you asked for: your account, your meetings, chat, recordings and sharing;',
          'our legitimate interests: keeping the service and its users safe, preventing abuse, and finding and fixing problems;',
          web && f.sold ? 'legal obligations: keeping payment records as tax law requires, and answering the authorities when the law requires it.' : 'legal obligations: answering the authorities when the law requires it.',
        ),
      ],
    },
    {
      id: 'sharing',
      title: 'Who we share it with',
      blocks: [
        p('Companies that provide a service to us. Each may use the data only to provide that service to us, and must protect it as this policy does:'),
        list(
          hosting ? `${hosting}: our servers and our database.` : 'The company that hosts our servers and our database.',
          f.services.email ? `${f.services.email}: delivers our emails.` : 'The company that delivers our emails.',
          recordings === 'aws' ? `Amazon Web Services: stores recordings${f.storage.cloudfront ? ' and delivers them to the people who may watch them' : ''}.` : null,
          recordings === 'other' ? 'The company that stores our recordings.' : null,
          web && f.sold ? 'Stripe: takes payments for plans.' : null,
        ),
        p('Others, only as described here:'),
        list(
          external.length > 0 ? `${either(external)}, when you sign in with ${external.length > 1 ? 'one of them' : 'it'}. It learns that you signed in to ${f.appName}.` : null,
          'The people in your meetings see your name, your video, and your messages, and hear you.',
          'The people you share a recording with can watch it, and download it if you allow it.',
          'When a meeting was arranged through another product (a CRM, for example), that product receives the meeting’s chat and recordings, and keeps them by its own rules.',
          'The app stores that distribute our mobile apps may send us crash reports from them, only if you allowed that on your device and without saying who you are.',
          'The authorities, when the law requires it.',
          'Whoever takes over the business, if it is sold. We would tell you first.',
        ),
      ],
    },
    {
      id: 'where',
      title: 'Where it is kept',
      blocks: [
        p(
          [
            hosting ? `Our servers and our database are run by ${hosting}.` : null,
            recordings === 'aws'
              ? `Recordings are kept with Amazon Web Services${region ? `, in its ${region} region` : ''}.`
              : recordings === 'other'
                ? 'Recordings are kept by the company that stores them for us.'
                : 'Recordings are kept on our own servers.',
            'If your data is kept in or sent to another country, we protect it as the law requires, for example with the European Commission’s standard contractual clauses.',
          ]
            .filter(Boolean)
            .join(' '),
        ),
      ],
    },
    {
      id: 'retention',
      title: 'How long we keep it',
      blocks: [
        list(
          'Your account: until you delete it.',
          f.signIn.includes('email')
            ? `A sign-up with an email address that is never confirmed: ${days(PENDING_SIGNUP_KEPT_DAYS)}. A link to confirm an address or choose a password: until it is used, or stops working (24 hours at most).`
            : null,
          `A signed-in device: until it is signed out, or after ${days(SIGNED_IN_UNUSED_DAYS)} without use. Its record is deleted ${days(SIGNED_OUT_DEVICE_KEPT_DAYS)} later.`,
          'Meeting links you created: until you delete your account. A link stops working after 30 days without use.',
          `${
            f.retention.meetingDays
              ? `A meeting’s history, with the names of the people who took part (guests too): ${days(f.retention.meetingDays)} after the meeting, or sooner if its owner deletes their account.`
              : 'A meeting’s history, with the names of the people who took part (guests too): until the meeting’s owner deletes their account.'
          } Someone who took part and then deletes their account is shown as “${DELETED_ACCOUNT_NAME}” from then on.`,
          `Chat: until the host deletes it${f.retention.chatDays ? `, or ${days(f.retention.chatDays)} after the meeting` : ''}, and in any case with the meeting’s history. A message also goes when its sender deletes their account.`,
          'Recordings: until whoever made one deletes it, or deletes their account. Who a recording is shared with goes with it.',
          `The emails we send: a copy for ${days(EMAIL_COPY_KEPT_DAYS)}, then only a record that an email was sent, with no address or words, for ${days(EMAIL_RECORD_KEPT_DAYS)}. An email with a link that works only once is deleted after a day.`,
          `What a connected product was sent: ${days(WEBHOOK_RECORD_KEPT_DAYS)}.`,
          `Reports: ${days(REPORT_KEPT_DAYS)} after we close them, longer only where the law requires it.`,
          `Technical records and app problem reports: ${days(f.retention.logDays)}.`,
          `Copies in our database backups: at most ${days(f.retention.backupDays)}.`,
          web && f.sold ? 'Payment records and invoices, at Stripe: as long as tax law requires.' : null,
        ),
      ],
    },
    {
      id: 'your-rights',
      title: 'Your choices and rights',
      blocks: [
        list(
          'See and copy your data: your meetings, their chat and your recordings are in the app, and you can save a chat and download your recordings. For anything else, write to us.',
          'Correct it: change your name in Settings.',
          'Sign out a device, or all your other devices at once, in Settings.',
          ['Delete it: delete a recording, a meeting’s chat, or your whole account (see ', { text: 'Deleting your account', link: '/privacy#delete-account' }, ').'],
          'Object to how we use it, or ask us to use it less, where the law gives you that right.',
          'Complain to the data protection authority where you live.',
        ),
        p(
          privacy ? `Write to ${privacy} for any of these. ` : '',
          'We answer within 30 days, and may first ask you to confirm it is you, for example by writing from the address of your account.',
        ),
      ],
    },
    {
      id: 'delete-account',
      title: 'Deleting your account',
      blocks: [
        p(
          `In Settings, choose Delete account at the bottom, and ${deletionConfirmText(f.signIn.includes('apple'))}.`,
          ...(web ? [' Without signing in, you can ask for it on the page ', { text: 'Delete your account', link: '/delete-account' } as Inline, '.'] : []),
          ' It is deleted straight away and cannot be undone.',
        ),
        { h3: 'What is deleted' },
        list(...deletion.deleted),
        ...(deletion.notAffected ? [p(deletion.notAffected)] : []),
        { h3: 'What is kept, and for how long' },
        list(...deletion.kept),
        ...(privacy ? [p(web ? `If you can’t sign in, use the page at ${f.appUrl}/delete-account, or write to ` : 'If you can’t sign in, write to ', mail(privacy), '.')] : []),
      ],
    },
    {
      id: 'security',
      title: 'How we protect it',
      blocks: [
        list(
          'Everything is encrypted on its way: HTTPS for the app, and DTLS-SRTP for audio and video.',
          'Calls are encrypted between your device and our servers, not end to end: our media server handles the audio and video to pass them on.',
          'Nobody can see or hear a meeting before its host admits them; our server enforces it.',
          'We keep what lets a device stay signed in, and the keys of links, only in a scrambled form.',
          'Only the people who run the service can reach the servers and the database.',
        ),
      ],
    },
    {
      id: 'children',
      title: 'Children',
      blocks: [
        p(
          `${f.appName} is for people ${f.minimumAge} and over, with an account or as a guest. If you think a child under ${f.minimumAge} gave us their data, ${privacy ? `write to ${privacy}` : 'tell us'} and we will delete it.`,
        ),
      ],
    },
    {
      id: 'changes',
      title: 'Changes to this policy',
      blocks: [p('We post changes here, with a new date at the top. For an important change, we email everyone with an account before it applies.')],
    },
    {
      id: 'contact',
      title: 'Contact',
      blocks: [
        list(
          privacy ? ['About your data: ', mail(privacy)] : null,
          f.contact.support && f.contact.support !== privacy ? ['Anything else: ', mail(f.contact.support)] : null,
          f.operator.address ? `By post: ${f.operator.name}, ${f.operator.address}` : null,
        ),
      ].filter((block) => !('list' in block) || block.list.length > 0),
    },
  ];

  return {
    title: 'Privacy policy',
    updated: PRIVACY_UPDATED,
    intro: [`This policy says what ${f.appName} collects about you, why, who else sees it, how long it is kept, and what you can do about it.`],
    sections: sections.filter((s) => s.blocks.length > 0),
  };
}

export function termsOfService(f: LegalFacts): LegalDocument {
  const web = !f.native;
  const external = externalSignIn(f);
  const support = f.contact.support;
  const reports = f.contact.reports ?? support;
  const hostWith = external.length > 0 && !f.signIn.includes('email') ? `You need a ${either(external)} account to host meetings.` : 'You need an account to host meetings.';

  const sections: LegalSection[] = [
    {
      id: 'who-we-are',
      title: 'Who we are',
      blocks: [
        p(
          `${f.appName} is a service of ${operatorLine(f)}. These terms are an agreement between you and ${f.operator.name} whenever you use ${f.appName}, on the web at ${f.appUrl.replace(/^https?:\/\//, '')} or in our mobile apps. Using it means you accept them, and our `,
          { text: 'Privacy policy', link: '/privacy' },
          ' says what we do with your data.',
        ),
        ...(reports ? [p('Anyone, the authorities included, can reach us about something said or shown on the service at ', mail(reports), '.')] : []),
      ],
    },
    {
      id: 'who-can-use',
      title: 'Who can use it',
      blocks: [
        list(
          `You must be ${f.minimumAge} or older to use ${f.appName}, with an account or as a guest.`,
          `${hostWith} Guests join without one.`,
        ),
      ],
    },
    {
      id: 'your-account',
      title: 'Your account',
      blocks: [
        list(
          'An account is for one person. Keep your way of signing in to yourself.',
          'You are responsible for what is done with your account.',
          'If you think someone else used it, sign out everywhere in Settings, and tell us.',
        ),
      ],
    },
    {
      id: 'meetings',
      title: 'Meetings and recording',
      blocks: [
        list(
          'You are responsible for the meetings you host and for who you admit to them.',
          'Only the host, or a co-host the host has allowed, can record. Everyone in the meeting is shown that recording is on.',
          'Make sure recording is lawful where you and your participants are, and get consent where the law requires it.',
          'Share a recording only with people who may see it.',
        ),
      ],
    },
    {
      id: 'rules',
      title: 'Rules',
      blocks: [
        p('There is no tolerance for objectionable content or abusive people. Do not:'),
        list(
          'harass, bully or threaten anyone;',
          'post or show hate speech, or anything that attacks people for who they are;',
          'share sexual content involving anyone under 18, which we report to the authorities, or sexual content of anyone without their consent;',
          'share anything illegal, or spam, scams or phishing;',
          'pretend to be someone else;',
          'spread malware, or try to break into accounts, meetings or our servers;',
          'record a meeting where the law does not allow it;',
          'disrupt meetings you were not invited to, or come back to one after a host removed you;',
          'create accounts or meetings automatically, or use our servers or our relay for anything but meetings;',
          'infringe anyone else’s rights, such as their copyright or their privacy.',
        ),
      ],
    },
    {
      id: 'reporting',
      title: 'Reporting, and what we do',
      blocks: [
        list(
          'A meeting’s host can mute someone, stop their video, remove them, lock the meeting and turn chat off. Someone removed can’t come back to that meeting with the same account or browser.',
          'Anyone in a meeting can report another person: open their menu (⋯) and choose Report. A chat message can be reported from its own ⋯, a meeting’s saved chat afterwards, and a shared recording from its page.',
          'Anyone in a meeting can choose Hide for me in another person’s menu: you no longer see or hear them, or their messages, for the rest of the meeting. They aren’t told.',
          reports
            ? ['To report something without the app, write to ', mail(reports), ' with the meeting’s code, when it happened and what happened.']
            : null,
          'We look at every report within 24 hours. We may remove what broke these rules, and suspend or close the account responsible.',
          'We tell the person who reported what we did. We tell the person affected what we did and why; if they think we got it wrong, they can reply within 6 months and we look again.',
        ),
      ],
    },
    {
      id: 'your-content',
      title: 'Your content',
      blocks: [
        list(
          'What you say, show and share in meetings stays yours.',
          `You allow us to store it, process it and send it to the people it is meant for, only to run ${f.appName}.`,
          'A recording belongs to whoever made it.',
        ),
      ],
    },
  ];

  if (f.sold && !web) {
    sections.push({
      id: 'plans',
      title: 'Plans',
      blocks: [
        list(
          'A meeting follows the plan of the person who created it, whoever joins.',
          'You can’t change your plan in the app.',
          'When a plan ends, the account goes back to the Free plan. Nothing you made is deleted.',
          support ? ['For a question about your plan, write to ', mail(support), '.'] : null,
        ),
      ],
    });
  } else if (f.sold) {
    sections.push({
      id: 'plans',
      title: 'Plans and payment',
      blocks: [
        list(
          'The Free plan costs nothing. Paid plans are charged in US dollars, by card, through Stripe, at the price shown when you buy.',
          'A paid plan is paid in advance, for a month or a year, and renews for the same period until you cancel.',
          'You can cancel at any time from “Your plan” in Settings. The plan stays until the end of the period you paid for and is not renewed.',
          'If a renewal payment fails, the card is tried again. If it cannot be collected, the account goes back to the Free plan. Nothing you made is deleted.',
          'Payments already made are not refunded, except where the law requires it.',
          support ? ['For a question about a payment or your plan, write to ', mail(support), '.'] : null,
        ),
      ],
    });
  }

  sections.push(
    {
      id: 'ending',
      title: 'Ending',
      blocks: [
        list(
          ['You can stop at any time, and delete your account in Settings (see ', { text: 'Deleting your account', link: '/privacy#delete-account' }, ').'],
          'We may suspend or close an account that breaks these terms. We tell its owner why, unless the law does not allow it.',
        ),
      ],
    },
    {
      id: 'availability',
      title: 'Availability',
      blocks: [
        list(
          'Call quality depends on your network. The app lowers video quality or switches to audio only to keep a call connected.',
          'Meeting links that are not used for 30 days expire.',
          `We work to keep ${f.appName} running, but can’t promise it never stops. We may change or end features, and tell you in good time about a change that matters.`,
        ),
      ],
    },
    {
      id: 'liability',
      title: 'Our responsibility',
      blocks: [
        list(
          `${f.appName} is provided as it is. As far as the law allows, we are not responsible for what people say or show in meetings, or for losses that come indirectly from using the service.`,
          'Nothing in these terms takes away a right that the law where you live gives you as a consumer.',
        ),
      ],
    },
    {
      id: 'changes',
      title: 'Changes to these terms',
      blocks: [p('We post changes here, with a new date at the top. For an important change, we email everyone with an account at least 30 days before it applies.')],
    },
  );

  if (f.operator.jurisdiction) {
    sections.push({
      id: 'law',
      title: 'Law',
      blocks: [
        p(
          `These terms are under the law of ${f.operator.jurisdiction}.${f.operator.courts ? ` Disputes about them go to ${f.operator.courts}.` : ''} If you live in a country whose law protects consumers more, that protection still applies to you.`,
        ),
      ],
    });
  }

  const contact = list(
    support ? ['Questions: ', mail(support)] : null,
    reports && reports !== support ? ['Reports: ', mail(reports)] : null,
    f.operator.address ? `By post: ${f.operator.name}, ${f.operator.address}` : null,
  );
  if ('list' in contact && contact.list.length > 0) sections.push({ id: 'contact', title: 'Contact', blocks: [contact] });

  return {
    title: 'Terms',
    updated: TERMS_UPDATED,
    intro: [`The rules for using ${f.appName}, in plain words.`],
    sections,
  };
}

/** Every piece of text in a document, one line per block, for tests and checks. */
export function legalText(doc: LegalDocument): string {
  const inline = (parts: readonly Inline[]) => parts.map((part) => (typeof part === 'string' ? part : part.text)).join('');
  const lines = [doc.title, inline(doc.intro)];
  for (const section of doc.sections) {
    lines.push(section.title);
    for (const block of section.blocks) {
      if ('p' in block) lines.push(inline(block.p));
      else if ('h3' in block) lines.push(block.h3);
      else lines.push(...block.list.map(inline));
    }
  }
  return lines.join('\n');
}
